import axios from "axios";
import { useEffect, useState } from "react";
import { FiUpload, FiEye, FiDownload, FiTrash2, FiAlertCircle } from "react-icons/fi";
import { HiOutlineCalendar } from "react-icons/hi";
import Heading from "../../components/Heading";
import { AiOutlineClose } from "react-icons/ai";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { baseApiURL } from "../../baseUrl";

const Material = ({ branch: lockedBranch }) => {
  const { fullname } = useSelector((state) => state.userData);
  const [allSubjects, setAllSubjects] = useState([]);
  const [branches, setBranches] = useState([]);
  const [file, setFile] = useState();
  const [noStudentsMessage, setNoStudentsMessage] = useState("");
  const [studentRegulations, setStudentRegulations] = useState([]);
  const [selected, setSelected] = useState({
    title: "",
    subject: "",
    branch: lockedBranch || "",
    semester: "",
    faculty: fullname?.split(" ")[0] + " " + fullname?.split(" ")[2] || fullname,
  });

  useEffect(() => {
    checkStudentPresence();
  }, [selected.semester, selected.branch]);

  const checkStudentPresence = async () => {
    if (!selected.semester) {
      setNoStudentsMessage("");
      return;
    }

    try {
      const payload = { semester: Number(selected.semester) };
      if (selected.branch) {
        payload.branch = selected.branch;
      }

      const response = await axios.post(`${baseApiURL()}/student/details/getDetails`, payload);
      if (response.data.success && response.data.user && response.data.user.length > 0) {
        const studentReg = response.data.user[0].regulation;
        if (studentReg && (!selected.regulation || selected.regulation === "")) {
          setSelected(prev => ({ ...prev, regulation: studentReg }));
        }
        setNoStudentsMessage("");
      } else {
        setNoStudentsMessage("no students are there for that semester");
        setSelected(prev => ({ ...prev, regulation: "" }));
      }
    } catch (error) {
      setNoStudentsMessage("no students are there for that semester");
      setSelected(prev => ({ ...prev, regulation: "" }));
    }
  };
  
  // State for viewing materials
  const [viewMode, setViewMode] = useState("upload"); // "upload" or "view"
  const [materials, setMaterials] = useState([]);
  const [selectedViewSubject, setSelectedViewSubject] = useState("");

  // Load subjects and branches on mount
  useEffect(() => {
    loadSubjects();
    loadBranches();
  }, []);

  // Load materials when filters change in view mode
  useEffect(() => {
    if (viewMode === "view") {
      getFacultyMaterials();
    }
  }, [viewMode, selectedViewSubject]);

  const loadSubjects = () => {
    toast.loading("Loading Subjects...");
    axios
      .get(`${baseApiURL()}/subject/getSubject`)
      .then((response) => {
        toast.dismiss();
        if (response.data.success) {
          console.log("Subjects loaded:", response.data.subject);
          setAllSubjects(response.data.subject);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.dismiss();
        toast.error(error.message);
      });
  };

  const loadBranches = () => {
    axios
      .get(`${baseApiURL()}/branch/getBranch`)
      .then((response) => {
        if (response.data.success) {
          console.log("Branches loaded:", response.data.branches);
          setBranches(response.data.branches);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  // Get materials (HODs see all in branch, Faculty see only theirs)
  const getFacultyMaterials = () => {
    let queryParams = new URLSearchParams();
    
    // If lockedBranch exists, we are in HOD mode - show all materials for the branch
    if (lockedBranch) {
      queryParams.append("branch", lockedBranch);
      if (selectedViewSubject) {
        queryParams.append("subject", selectedViewSubject);
      }
      toast.loading("Loading department materials...");
      
      axios
        .get(`${baseApiURL()}/material/getAllMaterials?${queryParams.toString()}`)
        .then((response) => {
          toast.dismiss();
          if (response.data.success) {
            setMaterials(response.data.materials);
            if (response.data.materials.length > 0) {
              toast.success(`Found ${response.data.materials.length} departmental materials`);
            }
          } else {
            toast.error(response.data.message || "No materials found for this department");
            setMaterials([]);
          }
        })
        .catch((error) => {
          toast.dismiss();
          console.error("Error loading department materials:", error);
          toast.error(error.response?.data?.message || "Network error");
          setMaterials([]);
        });
    } else {
      // Original faculty mode - show only their own materials
      const facultyName = selected.faculty;
      queryParams.append("faculty", facultyName);
      if (selectedViewSubject) {
        queryParams.append("subject", selectedViewSubject);
      }
      
      toast.loading("Loading your materials...");
      
      axios
        .get(`${baseApiURL()}/material/getFacultyMaterials?${queryParams.toString()}`)
        .then((response) => {
          toast.dismiss();
          if (response.data.success) {
            setMaterials(response.data.materials);
            if (response.data.materials.length > 0) {
              toast.success(`Found ${response.data.materials.length} materials`);
            }
          } else {
            toast.error(response.data.message || "Failed to load materials");
            setMaterials([]);
          }
        })
        .catch((error) => {
          toast.dismiss();
          console.error("Error loading faculty materials:", error);
          toast.error(error.response?.data?.message || "Network error");
          setMaterials([]);
        });
    }
  };

  // Add material handler
  const addMaterialHandler = () => {
    if (!selected.branch || !selected.semester) {
      return toast.error("Please select both Branch and Semester!");
    }

    if (!file) {
      return toast.error("Please select a file to upload!");
    }

    if (!selected.title) {
      return toast.error("Please enter material title!");
    }

    if (!selected.subject) {
      return toast.error("Please select a subject!");
    }

    const formData = new FormData();
    formData.append("title", selected.title);
    formData.append("subject", selected.subject);
    formData.append("branch", selected.branch);
    formData.append("semester", selected.semester);
    formData.append("faculty", selected.faculty);
    formData.append("material", file);

    toast.loading("Uploading Material...");
    
    axios
      .post(`${baseApiURL()}/material/addMaterial`, formData)
      .then((response) => {
        toast.dismiss();
        if (response.data.success) {
          toast.success(response.data.message);
          setSelected({
            title: "",
            subject: "",
            branch: "",
            semester: "",
            faculty: selected.faculty,
          });
          setFile(null);
          
          // Refresh materials if in view mode
          if (viewMode === "view") {
            getFacultyMaterials();
          }
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.dismiss();
        console.error("Upload error:", error);
        toast.error(error.response?.data?.message || error.response?.data?.details || "Upload failed");
      });
  };

  // Delete material handler
  const deleteMaterialHandler = (materialId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    toast.loading("Deleting material...");
    
    axios
      .delete(`${baseApiURL()}/material/deleteMaterial/${materialId}`)
      .then((response) => {
        toast.dismiss();
        if (response.data.success) {
          toast.success("Material deleted successfully!");
          getFacultyMaterials(); // Refresh the list
        } else {
          toast.error(response.data.message || "Failed to delete material");
        }
      })
      .catch((error) => {
        toast.dismiss();
        console.error("Delete error:", error);
        toast.error(error.response?.data?.message || "Error deleting material");
      });
  };

  // Download handler
  const handleDownload = async (item) => {
    if (!item.link) return;
    
    try {
      let url = item.link;
      
      if (url.includes('cloudinary.com')) {
        if (!url.includes('upload/')) {
          toast.error("Invalid file URL");
          return;
        }
        
        const downloadUrl = url + (url.includes('?') ? '&' : '?') + 'fl=attachment';
        
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        let filename = item.title || 'download';
        if (url.includes('/')) {
          const urlParts = url.split('/');
          const lastPart = urlParts[urlParts.length - 1];
          if (lastPart.includes('.')) {
            filename = lastPart.split('?')[0];
          }
        }
        
        filename = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
        if (!filename.includes('.')) {
          filename += '.pdf';
        }
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(`Downloading ${filename}...`);
      } else {
        window.open(url, '_blank');
        toast.success("Opening file...");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to access file. Please try again.");
    }
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Date not available";
      }
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      return "Invalid date";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bento-header-banner flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Study Material Management</h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium text-xs mt-0.5">Upload study materials, syllabus documents & notes for enrolled students</p>
        </div>
      </div>

      {/* Sub-tab Navigation */}
      <div className="bento-card p-1.5 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setViewMode("upload")}
            className={`px-4 py-2 rounded-xl font-bold text-xs transition cursor-pointer ${
              viewMode === "upload"
                ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200/60 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <FiUpload className="w-4 h-4" />
              <span>Upload Material</span>
            </div>
          </button>
          <button
            onClick={() => {
              setViewMode("view");
              getFacultyMaterials();
            }}
            className={`px-4 py-2 rounded-xl font-bold text-xs transition cursor-pointer ${
              viewMode === "view"
                ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200/60 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <FiEye className="w-4 h-4" />
              <span>View & Manage Uploads</span>
            </div>
          </button>
        </div>
      </div>

      {/* Upload View */}
      {viewMode === "upload" && (
        <div className="w-full flex justify-center items-center">
          <div className="w-full max-w-2xl flex flex-col justify-center items-center bg-white dark:bg-slate-900 p-6 md:p-8 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800">
            {/* Material Title */}
            <div className="w-full">
              <label htmlFor="title" className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Material Title *
              </label>
              <input
                type="text"
                id="title"
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                value={selected.title}
                onChange={(e) =>
                  setSelected({ ...selected, title: e.target.value })
                }
                placeholder="Enter material title"
              />
            </div>

            {/* Select Branch */}
            <div className="w-full mt-4">
              <label htmlFor="branch" className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Select Branch *
              </label>
              <select
                value={selected.branch}
                id="branch"
                disabled={!!lockedBranch}
                className={`w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none ${lockedBranch ? 'opacity-50 cursor-not-allowed' : ''}`}
                onChange={(e) =>
                  setSelected({ ...selected, branch: e.target.value })
                }
              >
                <option value="">-- Select Branch --</option>
                {branches.map((branch) => (
                  <option key={branch._id || branch.name} value={branch.name}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Select Semester */}
            <div className="w-full mt-4">
              <label htmlFor="semester" className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Select Semester *
              </label>
              <select
                value={selected.semester}
                id="semester"
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                onChange={(e) =>
                  setSelected({ ...selected, semester: e.target.value })
                }
              >
                <option value="">-- Select Semester --</option>
                {Array.from({ length: 8 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    Semester {i + 1}
                  </option>
                ))}
              </select>
              {noStudentsMessage && (
                <div className="w-full mt-2.5 p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl text-amber-800 dark:text-amber-300 text-xs flex items-center space-x-2">
                  <FiAlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                  <span className="font-medium">{noStudentsMessage}</span>
                </div>
              )}
            </div>

            {/* Select Regulation */}
            <div className="w-full mt-4">
              <label htmlFor="regulation" className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Select Regulation
              </label>
              <select
                value={selected.regulation || ""}
                id="regulation"
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                onChange={(e) =>
                  setSelected({ ...selected, regulation: e.target.value })
                }
              >
                <option value="">-- All Regulations --</option>
                {(studentRegulations.length > 0 ? studentRegulations : Array.from(new Set(allSubjects.map(s => s.regulation).filter(Boolean)))).map((reg) => (
                  <option key={reg} value={reg}>
                    {reg}
                  </option>
                ))}
              </select>
            </div>

            {/* Select Subject */}
            <div className="w-full mt-4">
              <label htmlFor="subject" className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Select Subject *
              </label>
              <select
                value={selected.subject}
                id="subject"
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                onChange={(e) =>
                  setSelected({ ...selected, subject: e.target.value })
                }
              >
                <option value="">-- Select Subject --</option>
                {(noStudentsMessage ? [] : allSubjects
                  .filter(item => !selected.branch || item.branch?.name === selected.branch || item.branch === selected.branch)
                  .filter(item => !selected.semester || String(item.semester) === String(selected.semester))
                  .filter(item => !selected.regulation || item.regulation?.toUpperCase() === selected.regulation.toUpperCase())
                ).map((item) => (
                  <option key={item._id} value={item.name}>
                    {item.name} {item.code ? `(${item.code})` : ''} {item.regulation ? `[${item.regulation}]` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* File Upload */}
            <div className="w-full mt-4">
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Upload File *
              </label>
              {!file ? (
                <label
                  htmlFor="upload"
                  className="w-full px-4 py-8 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-indigo-500 dark:hover:border-indigo-500 bg-slate-50 dark:bg-slate-950 transition-colors"
                >
                  <FiUpload className="text-2xl text-slate-400 dark:text-slate-500 mb-2" />
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Click to upload or drag and drop</span>
                  <span className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">PDF, PPT, DOC (Max 50MB)</span>
                </label>
              ) : (
                <div className="w-full px-4 py-3 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 rounded-xl flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">{file.name}</span>
                  <button
                    onClick={() => setFile(null)}
                    className="text-rose-500 hover:text-rose-700 dark:hover:text-rose-400 cursor-pointer"
                  >
                    <AiOutlineClose />
                  </button>
                </div>
              )}
              <input
                type="file"
                id="upload"
                hidden
                accept=".pdf,.ppt,.pptx,.doc,.docx"
                onChange={(e) => setFile(e.target.files[0])}
              />
            </div>

            <button
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white mt-6 px-6 py-2.5 rounded-xl transition shadow-xs font-bold text-xs cursor-pointer"
              onClick={addMaterialHandler}
            >
              Upload Material
            </button>
          </div>
        </div>
      )}

      {/* View Materials View */}
      {viewMode === "view" && (
        <div className="w-full space-y-4">
          {/* Filters Section */}
          <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800">
            <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-3">Filter Your Materials</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <select
                  value={selectedViewSubject}
                  onChange={(e) => setSelectedViewSubject(e.target.value)}
                  className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                >
                  <option value="">All Subjects</option>
                  {allSubjects.map((item) => (
                    <option key={item._id} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <button
                  onClick={() => {
                    setSelectedViewSubject("");
                  }}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Clear Filter
                </button>
              </div>
            </div>
          </div>

          {/* Materials List */}
          <div className="space-y-3">
            {materials.length > 0 ? (
              materials.map((item) => (
                <div
                  key={item._id}
                  className="border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs hover:shadow-md transition bg-white dark:bg-slate-900"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 space-y-2">
                      <h3 className="text-base font-bold text-slate-900 dark:text-white">
                        {item.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 font-bold px-2.5 py-0.5 rounded-lg border border-indigo-100 dark:border-indigo-800">
                          {item.subject}
                        </span>
                        <span className="bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 font-bold px-2.5 py-0.5 rounded-lg border border-emerald-100 dark:border-emerald-800">
                          {item.branch}
                        </span>
                        <span className="bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 font-bold px-2.5 py-0.5 rounded-lg border border-purple-100 dark:border-purple-800">
                          Semester {item.semester}
                        </span>
                        <span className="flex items-center text-slate-400 dark:text-slate-500 font-medium text-[11px] ml-auto">
                          <HiOutlineCalendar className="mr-1" />
                          {formatDate(item.createdAt)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                        Uploaded by: <span className="text-slate-800 dark:text-slate-200 font-bold">{item.faculty}</span>
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-1.5 ml-2">
                      {item.link && (
                        <button
                          onClick={() => handleDownload(item)}
                          className="p-2 bg-indigo-50 dark:bg-indigo-950/60 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 text-indigo-600 dark:text-indigo-400 rounded-xl transition cursor-pointer"
                          title="Download"
                        >
                          <FiDownload size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => deleteMaterialHandler(item._id, item.title)}
                        className="p-2 bg-rose-50 dark:bg-rose-950/60 hover:bg-rose-100 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-400 rounded-xl transition cursor-pointer"
                        title="Delete"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
                <div className="text-slate-300 dark:text-slate-700 mb-2">
                  <FiEye className="w-12 h-12 mx-auto" />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1">No materials found</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {selectedViewSubject
                    ? "Try selecting a different subject"
                    : "You haven't uploaded any materials yet"}
                </p>
                {!selectedViewSubject && (
                  <button
                    onClick={() => setViewMode("upload")}
                    className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl transition shadow-xs cursor-pointer"
                  >
                    Upload Your First Material
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Summary */}
          {materials.length > 0 && (
            <div className="mt-4 text-xs font-medium text-slate-500 dark:text-slate-400 text-center">
              Showing {materials.length} material{materials.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Material;